import React, { useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useAuth, useGuard } from '../../hooks/auth'
import {
  BORDERO_MAX_DIAS_ASYNC,
  BORDERO_MAX_DIAS_SYNC,
  borderoDias,
  useBordero,
  useBorderoPdvs,
} from '../../hooks/bordero'
import EventPicker, { PickedEvent } from '../../components/EventComparison/EventPicker'
import ProducerPicker, { PickedProducer } from '../../components/EventComparison/ProducerPicker'
import IndicatorCard from '../../components/DashboardCharts/IndicatorCard'
import PdvPicker from './PdvPicker'
import SetorDrill from './SetorDrill'
import { APIDecoratorWithBaseURI } from '../../Service/api'
import {
  Page,
  PageTitle,
  PageSub,
  FiltersCard,
  FilterField,
  FieldLabel,
  TableScroll,
  Table,
  MiniSeg,
  MiniBtn,
} from '../../components/EventComparison/styles'
import { StackedBar, HBars, WeekBars, LineChart, LinePoint, SERIES } from './charts'

const ACCENT = SERIES.accent

// Monograma BD (quadrado) para o spinner — os logos de assets são o wordmark largo.
const BD_MARK = `${process.env.PUBLIC_URL || ''}/favicon.svg`

const fmtInt = (n: number) => Math.round(Number(n) || 0).toLocaleString('pt-BR')
const fmtBRL = (n: number) =>
  'R$ ' + (Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtPct = (n: number) => (Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%'
const compactBRL = (v: number) => {
  const n = Number(v) || 0
  if (n >= 1e6) return 'R$ ' + (n / 1e6).toFixed(2).replace('.', ',') + ' mi'
  if (n >= 1e3) return 'R$ ' + Math.round(n / 1e3) + ' mil'
  return fmtBRL(n)
}
const Money = ({ v }: { v: number }) => (
  <span title={fmtBRL(v)} style={{ cursor: 'help' }}>
    {compactBRL(v)}
  </span>
)
/** 'YYYY-MM-DD' -> 'DD/MM' */
const fmtDM = (iso: string) => {
  const p = (iso || '').slice(0, 10).split('-')
  return p.length === 3 ? `${p[2]}/${p[1]}` : iso
}

/* ── styled locais ─────────────────────────────────────────────── */
const SectionTitle = styled.h2`
  font-size: 15px;
  font-weight: 680;
  color: #212121;
  margin: 36px 0 14px;
`
const CardTools = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 14px;
`
const AccumLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  color: #616161;
  cursor: pointer;
  input {
    accent-color: #B0020B;
    cursor: pointer;
  }
`

/* ── loading animado (logo espelhando + mensagens) ── */
const flip = keyframes`
  from { transform: rotateY(0deg); }
  to { transform: rotateY(360deg); }
`
const LoadBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px 12px;
`
const SpinLogo = styled.img`
  width: 56px;
  height: 56px;
  animation: ${flip} 1.6s ease-in-out infinite;
`
const LoadMsg = styled.div`
  font-size: 14px;
  font-weight: 560;
  color: #616161;
  min-height: 20px;
`
const LOADING_MSGS = [
  'Aquecendo os motores…',
  'Apurando os números…',
  'Contabilizando as receitas…',
  'Separando os ingressos…',
  'Conferindo os pedidos…',
  'Somando as taxas…',
  'Fechando o borderô…',
]
const JobProgressWrap = styled.div`
  width: min(420px, 90%);
  margin-top: 6px;
  text-align: center;
`
const JobProgressLabel = styled.div`
  font-size: 12.5px;
  color: #98a2b3;
  margin-top: 6px;
  b {
    color: #b0020b;
    font-variant-numeric: tabular-nums;
  }
`
/**
 * Loading do Borderô: logo BD girando + mensagens rotativas. No job assíncrono
 * (consolidado > 45 dias) ganha a barra de progresso com as etapas do worker.
 */
const BorderoLoading: React.FC<{
  job?: boolean
  progress?: { done: number; total: number } | null
}> = ({ job, progress }) => {
  const [i, setI] = useState(0)
  React.useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % LOADING_MSGS.length), 1400)
    return () => clearInterval(t)
  }, [])
  const pct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0
  return (
    <LoadBox>
      <SpinLogo src={BD_MARK} alt="Carregando" />
      <LoadMsg>{LOADING_MSGS[i]}</LoadMsg>
      {job && (
        <JobProgressWrap>
          <ProgressTrack>
            {progress ? <ProgressFill $pct={pct} /> : <ProgressIndeterminate />}
          </ProgressTrack>
          <JobProgressLabel>
            {progress ? (
              <>
                <b>{pct}%</b> — {progress.done} de {progress.total} etapas concluídas
              </>
            ) : (
              'Preparando o processamento em segundo plano…'
            )}{' '}
            · pode levar alguns minutos, a página atualiza sozinha
          </JobProgressLabel>
        </JobProgressWrap>
      )}
    </LoadBox>
  )
}

/* ── export PDF ── */
const ExportBtn = styled.button`
  background: #fff;
  color: #B0020B;
  border: 1px solid #B0020B;
  border-radius: 11px;
  padding: 9px 18px;
  font-size: 13.5px;
  font-weight: 620;
  cursor: pointer;
  &:hover {
    background: rgba(176, 2, 11, 0.06);
  }
`
const TopBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 8px 0 4px;
`
const ValueSeg = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin: 4px 0 12px;
  flex-wrap: wrap;
`
const DateInput = styled.input`
  border: 1px solid #E0E0E0;
  background: #F9F9F9;
  border-radius: 11px;
  padding: 9px 12px;
  font-size: 13px;
  color: #212121;
  width: 100%;
`
const FilterBtn = styled.button`
  align-self: flex-end;
  background: ${ACCENT};
  color: #fff;
  border: none;
  border-radius: 11px;
  padding: 11px 26px;
  font-size: 14px;
  font-weight: 640;
  cursor: pointer;
  height: 42px;
  &:hover:not(:disabled) {
    background: #990009;
  }
  &:disabled {
    background: #E0E0E0;
    color: #98a2b3;
    cursor: not-allowed;
  }
`
const Hint = styled.div`
  color: #98a2b3;
  font-size: 13px;
  text-align: center;
  padding: 40px 12px;
`
const QuickRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`
const QuickBtn = styled.button<{ $ghost?: boolean }>`
  background: ${(p) => (p.$ghost ? 'transparent' : '#F5F5F5')};
  color: ${(p) => (p.$ghost ? '#B0020B' : '#424242')};
  border: 1px solid ${(p) => (p.$ghost ? 'rgba(176, 2, 11, 0.4)' : '#E0E0E0')};
  border-radius: 9px;
  padding: 7px 13px;
  font-size: 12.5px;
  font-weight: 560;
  cursor: pointer;
  &:hover {
    background: ${(p) => (p.$ghost ? 'rgba(176, 2, 11, 0.06)' : '#EEEEEE')};
  }
`
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(168px, 1fr));
  gap: 12px;
  margin-top: 20px;
`
const Kpi = styled.div<{ $primary?: boolean }>`
  background: #fff;
  border: 1px solid ${(p) => (p.$primary ? ACCENT : '#E0E0E0')};
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(33, 33, 33, 0.08), 0 1px 2px rgba(33, 33, 33, 0.05);
  padding: 16px;
  position: relative;
  &:hover .kpi-tt {
    opacity: 1;
    visibility: visible;
  }
`
const KpiTip = styled.div`
  position: absolute;
  z-index: 30;
  left: 0;
  bottom: calc(100% + 6px);
  min-width: 240px;
  background: #101828;
  color: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 400;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.24);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.1s;
`
const KpiLabel = styled.div`
  font-size: 12px;
  font-weight: 560;
  color: #616161;
`
const KpiValue = styled.div<{ $primary?: boolean }>`
  font-size: 21px;
  font-weight: 720;
  letter-spacing: -0.02em;
  margin-top: 10px;
  color: ${(p) => (p.$primary ? ACCENT : '#212121')};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`
const KpiHint = styled.div`
  font-size: 12px;
  color: #98A2B3;
  margin-top: 6px;
`
const DrillLink = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 10px 4px;
  font-size: 13px;
  color: #212121;
  text-align: left;
  .nome {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .val {
    font-weight: 650;
    font-variant-numeric: tabular-nums;
  }
  &:hover {
    color: #B0020B;
  }
`
const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`
const Dl = styled.dl`
  margin: 0;
  dt {
    font-weight: 700;
    color: #101828;
    margin-top: 12px;
    font-size: 13.5px;
  }
  dt:first-child {
    margin-top: 0;
  }
  dd {
    margin: 2px 0 0;
    font-size: 13px;
    color: #475467;
  }
`
const Note = styled.div`
  background: #F9F9F9;
  border: 1px solid #E0E0E0;
  border-radius: 14px;
  padding: 14px 16px;
  font-size: 12.5px;
  line-height: 1.55;
  color: #616161;
  margin-top: 18px;
  b {
    color: #212121;
  }
`
/* Barra de progresso do job assíncrono (consolidado > 45 dias). */
const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: rgba(176, 2, 11, 0.12);
  overflow: hidden;
  margin-top: 10px;
  position: relative;
`
const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  border-radius: 4px;
  background: #b0020b;
  width: ${(p) => Math.max(3, Math.min(100, p.$pct))}%;
  transition: width 0.8s ease;
`
const ProgressIndeterminate = styled.div`
  position: absolute;
  inset: 0;
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 30%;
    border-radius: 4px;
    background: #b0020b;
    animation: bordero-progress-slide 1.4s ease-in-out infinite;
  }
  @keyframes bordero-progress-slide {
    0% {
      left: -30%;
    }
    100% {
      left: 100%;
    }
  }
`
const PeriodBanner = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: rgba(176, 2, 11, 0.05);
  border: 1px solid rgba(176, 2, 11, 0.25);
  border-radius: 14px;
  padding: 13px 16px;
  font-size: 13px;
  line-height: 1.5;
  color: #616161;
  margin: 12px 0 4px;
  b {
    color: #B0020B;
  }
`

interface Props {
  permissions?: string[]
}

interface Applied {
  eventIds: number[]
  clienteId?: number
  startDate?: string
  endDate?: string
  pdvIds: number[]
  ignorePracaTestes: boolean
}

export function Bordero({ permissions = ['DP20'] }: Props) {
  useGuard(permissions)
  const authContext = useAuth()
  const isStaff = !!authContext?.user && authContext.user.clienteId == null

  // seleção pendente (só vira consulta ao clicar em Filtrar)
  const [producer, setProducer] = useState<PickedProducer | null>(null)
  const [events, setEvents] = useState<PickedEvent[]>([])
  const [allEvents, setAllEvents] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [pdvIds, setPdvIds] = useState<number[]>([])
  const [ignorarTestes, setIgnorarTestes] = useState(true)
  const [applied, setApplied] = useState<Applied | null>(null)

  // drill-down por setor/ingresso (detalhamento e receita por evento)
  const [drillEvent, setDrillEvent] = useState<Record<number, boolean>>({})
  const [receitaDrill, setReceitaDrill] = useState<number | null>(null)
  const toggleDrill = (id: number) => setDrillEvent((o) => ({ ...o, [id]: !o[id] }))

  // controles do gráfico de evolução (ritmo de vendas)
  const [trendGran, setTrendGran] = useState<'dia' | 'semana'>('dia')
  const [trendMetric, setTrendMetric] = useState<'ingressos' | 'receita'>('receita')
  const [trendCumulative, setTrendCumulative] = useState(false)
  // "Somente ingressos": mostra face (bilhetes) sem taxas/juros/seguro
  const [soFace, setSoFace] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Baixa o PDF gerado no servidor (bd-services-pdf-generator) pela URL assinada.
  const exportPdf = async () => {
    if (!applied || exporting) return
    setExporting(true)
    try {
      const parts: string[] = []
      if (applied.eventIds.length) parts.push(`eventIds=${applied.eventIds.join(',')}`)
      else if (applied.clienteId) parts.push(`clienteId=${applied.clienteId}`)
      if (applied.startDate && applied.endDate)
        parts.push(`startDate=${applied.startDate}&endDate=${applied.endDate}`)
      if (applied.pdvIds.length) parts.push(`pdvIds=${applied.pdvIds.join(',')}`)
      if (!applied.ignorePracaTestes) parts.push('ignorePracaTestes=0')
      if (soFace) parts.push('soFace=1')
      const res: any = await APIDecoratorWithBaseURI().get(
        `producer/analytics/borderoPdf?${parts.join('&')}`
      )
      const url = res?.data?.data?.url ?? res?.data?.body?.url
      if (url) window.open(url, '_blank')
    } finally {
      setExporting(false)
    }
  }

  const selectedIds = useMemo(() => events.map((e) => e.id), [events])

  // seleção pendente que alimenta a lista de PDVs. Produtor selecionado sem
  // evento explícito = escopo do produtor (equivale a "Todos os Eventos");
  // sem produtor E sem evento, só a janela → consolidado global (Top N + "Outros").
  const pendingEventIds = allEvents ? [] : selectedIds
  const pendingClienteId = pendingEventIds.length === 0 && producer ? producer.id : undefined
  const hasPeriod = !!(startDate && endDate)

  // Consolidado global (sem produtor e sem evento): único caso com teto de
  // janela — 45 dias síncrono; acima disso vai para o job assíncrono (máx. 12
  // meses). Com produtor/evento selecionado não há limite de período.
  const isGlobalPending = !producer && pendingEventIds.length === 0
  const diasPending = hasPeriod ? borderoDias(startDate, endDate) : 0
  const periodoExcedido = isGlobalPending && hasPeriod && diasPending > BORDERO_MAX_DIAS_ASYNC

  const canFilter = (selectedIds.length > 0 || !!producer || hasPeriod) && !periodoExcedido

  const { pdvs, pdvsLoading } = useBorderoPdvs(pendingEventIds, pendingClienteId, ignorarTestes)

  const applyFilters = () => {
    if (!canFilter) return
    setApplied({
      eventIds: pendingEventIds,
      clienteId: pendingClienteId,
      startDate: startDate && endDate ? startDate : undefined,
      endDate: startDate && endDate ? endDate : undefined,
      pdvIds,
      ignorePracaTestes: ignorarTestes,
    })
  }

  // Atalhos de período (preenchem as datas; o usuário confirma em Filtrar).
  const toISO = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const setHoje = () => {
    const now = new Date()
    setStartDate(toISO(now))
    setEndDate(toISO(now))
  }
  const setUltimos7Dias = () => {
    const now = new Date()
    setStartDate(toISO(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6)))
    setEndDate(toISO(now))
  }
  const setMesAtual = () => {
    const now = new Date()
    setStartDate(toISO(new Date(now.getFullYear(), now.getMonth(), 1)))
    setEndDate(toISO(now))
  }
  const setMesPassado = () => {
    const now = new Date()
    setStartDate(toISO(new Date(now.getFullYear(), now.getMonth() - 1, 1)))
    setEndDate(toISO(new Date(now.getFullYear(), now.getMonth(), 0)))
  }
  const limparFiltros = () => {
    setProducer(null)
    setEvents([])
    setAllEvents(false)
    setStartDate('')
    setEndDate('')
    setPdvIds([])
    setIgnorarTestes(true)
    setApplied(null)
    setDrillEvent({})
    setReceitaDrill(null)
  }

  const { bordero, borderoLoading, borderoError, borderoJob, borderoRetry } = useBordero({
    eventIds: applied?.eventIds ?? [],
    clienteId: applied?.clienteId,
    startDate: applied?.startDate,
    endDate: applied?.endDate,
    pdvIds: applied?.pdvIds,
    ignorePracaTestes: applied?.ignorePracaTestes,
  })

  const loading = !!applied && borderoLoading && !bordero
  const t = bordero?.totais
  const c = bordero?.complementares
  const hasFaceSplit = t?.transacionadoFace != null && t?.naoTransacionadoFace != null
  const useFaceSplit = soFace && hasFaceSplit
  const transacionadoShown = (useFaceSplit ? t?.transacionadoFace : t?.transacionado) ?? 0
  const naoTransacionadoShown = (useFaceSplit ? t?.naoTransacionadoFace : t?.naoTransacionado) ?? 0
  const transacionadoBase = (useFaceSplit ? t?.valorFace : t?.totalCompras) ?? 0
  const isPeriodMode = bordero?.meta?.modo === 'periodo'
  const produtores = bordero?.produtores ?? []

  // Derivados p/ KPIs e "leituras" — a partir das categorias de método.
  const metodos = useMemo(() => bordero?.metodos ?? [], [bordero])
  const metodoVal = (m: { total: number; face?: number }) => (soFace && m.face != null ? m.face : m.total)
  const totalMetodos = useMemo(
    () => metodos.reduce((s, m) => s + (soFace && m.face != null ? m.face : m.total), 0),
    [metodos, soFace]
  )
  const findCat = (name: string) => metodos.find((m) => m.categoria === name)
  const cartaoParcelado = findCat('Cartão parcelado')
  const pedidosParcelados = cartaoParcelado?.pedidos ?? 0
  const valorParcelado = cartaoParcelado?.total ?? 0
  const pixCat = findCat('PIX')
  const nuPayCat = findCat('NuPay')
  const totalPix = pixCat ? metodoVal(pixCat) : 0
  const totalNuPay = nuPayCat ? metodoVal(nuPayCat) : 0
  const totalCartao = metodos.filter((m) => m.categoria.startsWith('Cartão')).reduce((s, m) => s + metodoVal(m), 0)

  // Faixas de parcelamento mais vendidas + parcela média (ponderada por pedidos).
  const topParcelas = useMemo(
    () => [...(bordero?.parcelas ?? [])].sort((a, b) => b.pedidos - a.pedidos).slice(0, 3),
    [bordero]
  )
  const parcelaMedia = useMemo(() => {
    const ps = bordero?.parcelas ?? []
    const tot = ps.reduce((s, p) => s + p.pedidos, 0)
    return tot > 0 ? ps.reduce((s, p) => s + p.parcelas * p.pedidos, 0) / tot : 0
  }, [bordero])

  const trendPoints: LinePoint[] = useMemo(() => {
    if (!bordero) return []
    const src =
      trendGran === 'dia'
        ? bordero.curvaDiaria.map((d) => ({
            x: fmtDM(d.dia),
            receita: soFace ? d.face : d.total,
            ingressos: d.bilhetes,
            pedidos: d.pedidos,
          }))
        : bordero.curvaSemanal.map((w) => ({
            x: fmtDM(w.inicio || w.semana),
            receita: soFace ? w.face : w.total,
            ingressos: w.bilhetes,
            pedidos: w.pedidos,
          }))
    const metricVal = (r: { receita: number; ingressos: number }) =>
      trendMetric === 'ingressos' ? r.ingressos : r.receita
    let acc = 0
    return src.map((r) => ({
      x: r.x,
      y: trendCumulative ? (acc += metricVal(r)) : metricVal(r),
      tip: { receita: r.receita, ingressos: r.ingressos, pedidos: r.pedidos },
    }))
  }, [bordero, trendGran, trendMetric, trendCumulative, soFace])
  const trendFormat = trendMetric === 'receita' ? compactBRL : fmtInt

  const compItems = t
    ? [
        { label: 'Ingressos', value: t.valorFace, color: SERIES.ingressos },
        { label: 'Taxa de conveniência', value: t.conveniencia, color: SERIES.conveniencia },
        { label: 'Juros de parcelamento', value: t.juros, color: SERIES.juros },
        { label: 'Seguro de ingressos', value: t.seguro, color: SERIES.seguro },
        { label: 'Taxa de transação', value: t.taxaProcessamento, color: SERIES.taxa },
      ]
    : []

  return (
    <Page>
      <PageTitle>Borderô</PageTitle>
      <PageSub>
        Relatório de performance de vendas por evento. Selecione um ou mais eventos (ou todos do
        produtor), ajuste período e PDV, e clique em <b>Filtrar</b> para consolidar bilhetes, o{' '}
        <b>valor total das compras</b> e os indicadores de venda.
      </PageSub>

      <FiltersCard>
        <FilterField>
          <FieldLabel>Produtor (opcional)</FieldLabel>
          <ProducerPicker
            value={producer}
            color={ACCENT}
            onChange={(p) => {
              setProducer(p)
              setEvents([])
              setPdvIds([])
            }}
          />
        </FilterField>
        <FilterField>
          <FieldLabel>
            Eventos{' '}
            <span style={{ textTransform: 'none', color: '#98a2b3', fontWeight: 500 }}>
              (some vários = a turnê)
            </span>
          </FieldLabel>
          <EventPicker
            label="evento"
            color={ACCENT}
            values={events}
            onChange={(evs) => {
              setAllEvents(false)
              setPdvIds([])
              setEvents(evs)
            }}
            clienteId={producer?.id}
            allOptionLabel="Todos os Eventos"
            allSelected={allEvents}
            onSelectAll={() => {
              setAllEvents(true)
              setEvents([])
              setPdvIds([])
            }}
          />
          {allEvents && !producer && (
            <span style={{ fontSize: 12, color: '#98A2B3', marginTop: 4 }}>
              Selecione um produtor para incluir todos os eventos.
            </span>
          )}
        </FilterField>

        <FilterField>
          <FieldLabel>Período (opcional)</FieldLabel>
          <div style={{ display: 'flex', gap: 8 }}>
            <DateInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <DateInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <QuickRow>
            <QuickBtn type="button" onClick={setHoje}>
              Hoje
            </QuickBtn>
            <QuickBtn type="button" onClick={setUltimos7Dias}>
              Últimos 7 dias
            </QuickBtn>
            <QuickBtn type="button" onClick={setMesAtual}>
              Mês atual
            </QuickBtn>
            <QuickBtn type="button" onClick={setMesPassado}>
              Mês passado
            </QuickBtn>
            <QuickBtn type="button" $ghost onClick={limparFiltros}>
              Limpar filtros
            </QuickBtn>
          </QuickRow>
          {isGlobalPending && !periodoExcedido && (
            <span style={{ fontSize: 12, color: '#98A2B3', marginTop: 4 }}>
              Consolidado geral (sem produtor): máximo de 12 meses. Acima de{' '}
              {BORDERO_MAX_DIAS_SYNC} dias, o relatório é processado em segundo plano e pode
              levar alguns minutos.
            </span>
          )}
          {periodoExcedido && (
            <span style={{ fontSize: 12, color: '#F04438', marginTop: 4 }}>
              Período acima de 12 meses — reduza a janela para filtrar.
            </span>
          )}
        </FilterField>

        <FilterField>
          <FieldLabel>PDV</FieldLabel>
          <PdvPicker
            options={pdvs}
            value={pdvIds}
            onChange={setPdvIds}
            loading={pdvsLoading}
            disabled={!canFilter}
          />
        </FilterField>

        <FilterField
          style={{
            gridColumn: '1 / -1',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: isStaff ? 'space-between' : 'flex-end',
          }}
        >
          {isStaff && (
            <AccumLabel>
              <input
                type="checkbox"
                checked={ignorarTestes}
                onChange={(e) => {
                  setIgnorarTestes(e.target.checked)
                  setPdvIds([])
                }}
              />
              Ignorar praça TESTES
            </AccumLabel>
          )}
          <FilterBtn type="button" disabled={!canFilter || loading} onClick={applyFilters}>
            {loading ? 'Filtrando…' : 'Filtrar'}
          </FilterBtn>
        </FilterField>
      </FiltersCard>

      {!applied && (
        <Hint>
          Selecione evento(s) ou um produtor — ou apenas um <b>período</b> (sem evento) para o
          consolidado de todos os eventos — e clique em Filtrar.
        </Hint>
      )}

      {borderoJob?.status === 'failed' && (
        <PeriodBanner>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span>
            <b>Falha ao consolidar o período.</b>{' '}
            <QuickBtn type="button" onClick={borderoRetry}>
              Tentar novamente
            </QuickBtn>
          </span>
        </PeriodBanner>
      )}

      {!!borderoError && !borderoJob && (
        <PeriodBanner>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span>
            <b>Não foi possível carregar o borderô.</b> Verifique os filtros e tente novamente.
          </span>
        </PeriodBanner>
      )}

      {loading && (
        <BorderoLoading
          job={borderoJob?.status === 'running'}
          progress={borderoJob?.status === 'running' ? borderoJob.progress : null}
        />
      )}

      {bordero && t && (
        <>
          <TopBar>
            <ExportBtn type="button" onClick={exportPdf} disabled={exporting}>
              {exporting ? 'Gerando PDF…' : '⬇ Exportar PDF'}
            </ExportBtn>
          </TopBar>

          {applied && !applied.startDate && (
            <PeriodBanner>
              <span style={{ fontSize: 16 }}>📅</span>
              <span>
                <b>Sem período selecionado</b> — exibindo <b>todo o histórico</b> de vendas da
                seleção. Para restringir, preencha as datas no filtro de período.
              </span>
            </PeriodBanner>
          )}

          {applied && !applied.ignorePracaTestes && (
            <PeriodBanner>
              <span style={{ fontSize: 16 }}>🧪</span>
              <span>
                <b>Inclui a praça TESTES</b> — as vendas dos eventos de teste estão somadas neste
                relatório. Marque “Ignorar praça TESTES” e filtre de novo para removê-las.
              </span>
            </PeriodBanner>
          )}

          {isPeriodMode && (
            <PeriodBanner>
              <span style={{ fontSize: 16 }}>📅</span>
              <span>
                <b>Consolidado do período</b> — todos os eventos com venda na janela
                {bordero.meta.totalEventos ? ` (${fmtInt(bordero.meta.totalEventos)} eventos)` : ''}. O
                detalhamento por evento e por produtor mostra os <b>Top {bordero.meta.topN ?? 10}</b>; os
                demais estão somados em <b>“Outros”</b>. Movimentação por data de pagamento.
              </span>
            </PeriodBanner>
          )}

          <div>
            {/* KPIs */}
            <SectionTitle style={{ marginTop: 8 }}>Indicadores consolidados</SectionTitle>
            <ValueSeg className="no-print">
              <AccumLabel>
                <input type="checkbox" checked={soFace} onChange={(e) => setSoFace(e.target.checked)} />
                Somente ingressos (sem taxas, juros e seguro)
              </AccumLabel>
            </ValueSeg>
            <KpiGrid>
              <Kpi $primary>
                <KpiLabel>{soFace ? 'Faturamento de ingressos' : 'Valor total das compras'}</KpiLabel>
                <KpiValue $primary><Money v={soFace ? t.valorFace : t.totalCompras} /></KpiValue>
                <KpiHint>
                  {soFace
                    ? 'só o preço dos ingressos — sem taxas, juros e seguro'
                    : 'pedidos web pagos'}
                </KpiHint>
              </Kpi>
              <Kpi>
                <KpiLabel>Valor de face (ingressos)</KpiLabel>
                <KpiValue><Money v={t.valorFace} /></KpiValue>
                <KpiHint>{fmtInt(t.pagos)} bilhetes pagos ativos · sem taxas</KpiHint>
              </Kpi>
              {!soFace && (
                <>
                  <Kpi>
                    <KpiLabel>Taxa de conveniência</KpiLabel>
                    <KpiValue><Money v={t.conveniencia} /></KpiValue>
                    <KpiHint>faturamento da operação</KpiHint>
                  </Kpi>
                  <Kpi>
                    <KpiLabel>Juros de parcelamento</KpiLabel>
                    <KpiValue><Money v={t.juros} /></KpiValue>
                    <KpiHint>{fmtInt(pedidosParcelados)} pedidos parcelados</KpiHint>
                  </Kpi>
                  <Kpi>
                    <KpiLabel>Seguro de ingressos</KpiLabel>
                    <KpiValue><Money v={t.seguro} /></KpiValue>
                    <KpiHint>
                      {fmtInt(t.pedidosComSeguro)} pedidos
                      {t.pedidosPagos > 0 ? ` · ${fmtPct((t.pedidosComSeguro / t.pedidosPagos) * 100)} de adesão` : ''}
                    </KpiHint>
                  </Kpi>
                  <Kpi>
                    <KpiLabel>Taxa de transação</KpiLabel>
                    <KpiValue><Money v={t.taxaProcessamento} /></KpiValue>
                    <KpiHint>taxa de processamento do checkout</KpiHint>
                  </Kpi>
                </>
              )}
              <Kpi>
                <KpiLabel>Ticket médio</KpiLabel>
                <KpiValue>{fmtBRL(t.ticketMedio)}</KpiValue>
                <KpiHint>por bilhete pago</KpiHint>
              </Kpi>
              <Kpi>
                <KpiLabel>Bilhetes pagos</KpiLabel>
                <KpiValue>{fmtInt(t.pagos)}</KpiValue>
                <KpiHint>
                  {fmtInt(t.cortesias)} cortesias · {fmtInt(t.pagos + t.cortesias)} ativos no total
                </KpiHint>
              </Kpi>
              <Kpi>
                <KpiLabel>Pedidos pagos</KpiLabel>
                <KpiValue>{fmtInt(t.pedidosPagos)}</KpiValue>
                <KpiHint>
                  {t.pedidosPagos > 0
                    ? `${fmtBRL((soFace ? t.valorFace : t.totalCompras) / t.pedidosPagos)} / pedido`
                    : '—'}
                </KpiHint>
              </Kpi>
              <Kpi>
                <KpiLabel>Compradores únicos</KpiLabel>
                <KpiValue>{fmtInt(c?.compradoresUnicos ?? 0)}</KpiValue>
                <KpiHint>por e-mail cadastrado</KpiHint>
              </Kpi>
              <Kpi>
                <KpiLabel>Transacionado por nós</KpiLabel>
                <KpiValue><Money v={transacionadoShown} /></KpiValue>
                <KpiHint>
                  {transacionadoBase > 0 ? fmtPct((transacionadoShown / transacionadoBase) * 100) : '—'} · passou pela BD
                </KpiHint>
                <KpiTip className="kpi-tt">
                  <b>Pode diferir dos créditos do fechamento financeiro.</b> Vendas
                  estornadas (chargeback) ficam de fora deste valor, mas no fechamento
                  entram como crédito, com o estorno lançado como débito à parte.
                  {!soFace &&
                    ' Além disso, este valor inclui taxas, juros e seguro — o fechamento credita só o valor de face dos ingressos.'}
                </KpiTip>
              </Kpi>
              <Kpi>
                <KpiLabel>Não transacionado</KpiLabel>
                <KpiValue><Money v={naoTransacionadoShown} /></KpiValue>
                <KpiHint>
                  {fmtInt(t.pedidosNaoTransacionados)} pedidos · dinheiro em PDV/comissário não-próprio
                </KpiHint>
              </Kpi>
            </KpiGrid>

            {/* Composição (só ao exibir o total das compras) */}
            {!soFace && (
              <IndicatorCard title="Composição do valor total das compras">
                <StackedBar items={compItems} />
                <div style={{ fontSize: 12.5, color: '#98a2b3', marginTop: 12 }}>
                  Conveniência e juros compõem o faturamento da plataforma. Além dos pagos, foram emitidas{' '}
                  {fmtInt(t.cortesias)} cortesias.
                </div>
              </IndicatorCard>
            )}

            {(t.naoTransacionado > 0 || t.transacionado > 0) && (
              <div style={{ marginTop: 16 }}>
                <IndicatorCard title="Transacionado × não transacionado">
                  <StackedBar
                    items={[
                      { label: 'Transacionado por nós', value: transacionadoShown, color: SERIES.ingressos },
                      { label: 'Não transacionado', value: naoTransacionadoShown, color: SERIES.juros },
                    ]}
                  />
                  <div style={{ fontSize: 12.5, color: '#98a2b3', marginTop: 12 }}>
                    <b>Não transacionado</b> = dinheiro em PDV ou comissário não-próprio (não passou pela BD):{' '}
                    {fmtBRL(naoTransacionadoShown)} em {fmtInt(t.pedidosNaoTransacionados)} pedidos.
                  </div>
                </IndicatorCard>
              </div>
            )}

          {/* Receita por evento */}
          <SectionTitle>Receita por evento</SectionTitle>
          <Grid2>
            <IndicatorCard title={soFace ? 'Faturamento de ingressos' : 'Valor total das compras'}>
              <HBars
                items={bordero.eventos.map((e) => ({
                  label: e.nome,
                  value: soFace ? e.valorFace : e.totalCompras,
                  valueLabel: compactBRL(soFace ? e.valorFace : e.totalCompras),
                  sub: [e.praca, e.uf].filter(Boolean).join(' · ') || undefined,
                }))}
              />
            </IndicatorCard>
            <IndicatorCard title="Bilhetes pagos">
              <HBars
                items={bordero.eventos.map((e) => ({
                  label: e.nome,
                  value: e.pagos,
                  valueLabel: fmtInt(e.pagos),
                  sub: `ticket médio ${fmtBRL(e.ticketMedio)}`,
                }))}
              />
            </IndicatorCard>
          </Grid2>

          {/* Abrir vendas por setor / ingresso (por evento) */}
          <div style={{ marginTop: 16 }}>
          <IndicatorCard title="Vendas por setor e ingresso">
            {bordero.eventos.map((e) => (
              <div key={e.eventoId} style={{ borderBottom: '1px solid #EDEDED' }}>
                <DrillLink
                  type="button"
                  disabled={e.eventoId === 0}
                  style={e.eventoId === 0 ? { cursor: 'default' } : undefined}
                  onClick={() =>
                    e.eventoId > 0 && setReceitaDrill((cur) => (cur === e.eventoId ? null : e.eventoId))
                  }
                >
                  <span style={{ color: '#B0020B', fontSize: 10 }}>
                    {e.eventoId === 0 ? '' : receitaDrill === e.eventoId ? '▾' : '▸'}
                  </span>
                  <span className="nome">{e.nome}</span>
                  <span className="val">{fmtBRL(soFace ? e.valorFace : e.totalCompras)}</span>
                </DrillLink>
                {e.eventoId > 0 && receitaDrill === e.eventoId && (
                  <div style={{ padding: '4px 0 12px' }}>
                    <SetorDrill
                      eventId={e.eventoId}
                      startDate={applied?.startDate}
                      endDate={applied?.endDate}
                      pdvIds={applied?.pdvIds}
                      ignorePracaTestes={applied?.ignorePracaTestes}
                    />
                  </div>
                )}
              </div>
            ))}
          </IndicatorCard>
          </div>

          {/* Ritmo de vendas — diário/semanal · ingressos/receita · acumulado */}
          <SectionTitle>Ritmo de vendas</SectionTitle>
          <IndicatorCard
            title={`Evolução ${trendGran === 'dia' ? 'diária' : 'semanal'} — ${
              trendMetric === 'receita' ? 'receita' : 'ingressos'
            }${trendCumulative ? ' (acumulado)' : ''}`}
          >
            <CardTools>
              <MiniSeg>
                <MiniBtn $active={trendGran === 'dia'} onClick={() => setTrendGran('dia')}>
                  Diário
                </MiniBtn>
                <MiniBtn $active={trendGran === 'semana'} onClick={() => setTrendGran('semana')}>
                  Semanal
                </MiniBtn>
              </MiniSeg>
              <MiniSeg>
                <MiniBtn
                  $active={trendMetric === 'ingressos'}
                  onClick={() => setTrendMetric('ingressos')}
                >
                  Ingressos
                </MiniBtn>
                <MiniBtn $active={trendMetric === 'receita'} onClick={() => setTrendMetric('receita')}>
                  Receita
                </MiniBtn>
              </MiniSeg>
              <AccumLabel>
                <input
                  type="checkbox"
                  checked={trendCumulative}
                  onChange={(e) => setTrendCumulative(e.target.checked)}
                />
                Acumulado
              </AccumLabel>
            </CardTools>
            <LineChart points={trendPoints} format={trendFormat} />
          </IndicatorCard>

          <div style={{ paddingTop: 16 }}>
            <IndicatorCard title="Vendas por semana (barras)">
              <WeekBars
                items={bordero.curvaSemanal.map((s) => ({
                  label: fmtDM(s.inicio || s.semana),
                  value: soFace ? s.face : s.total,
                  valueLabel: compactBRL(soFace ? s.face : s.total),
                  sub: `${fmtInt(s.pedidos)} pedidos`,
                }))}
              />
            </IndicatorCard>
          </div>

          {/* Meios de pagamento */}
          <SectionTitle>Meios de pagamento e parcelamento</SectionTitle>
          <Grid2>
            <IndicatorCard title="Métodos de pagamento">
              <HBars
                items={metodos.map((m) => ({
                  label: m.categoria,
                  value: metodoVal(m),
                  valueLabel: `${compactBRL(metodoVal(m))} · ${fmtPct(totalMetodos ? (metodoVal(m) / totalMetodos) * 100 : 0)}`,
                  sub: `${fmtInt(m.pedidos)} pedidos`,
                  tooltip:
                    m.categoria === 'Cartão parcelado' && bordero.parcelas.length ? (
                      <div>
                        <b>Distribuição de parcelas</b>
                        {bordero.parcelas.map((p) => (
                          <div key={p.parcelas} style={{ marginTop: 3 }}>
                            {p.parcelas}x — {fmtInt(p.pedidos)} pedidos · {compactBRL(metodoVal(p))}
                          </div>
                        ))}
                      </div>
                    ) : undefined,
                }))}
              />
            </IndicatorCard>
            <IndicatorCard title="Leituras de crédito e demanda">
              <Dl>
                <dt>PIX: {fmtPct(totalMetodos ? (totalPix / totalMetodos) * 100 : 0)} do volume</dt>
                <dd>
                  {fmtBRL(totalPix)} — cartão soma {fmtPct(totalMetodos ? (totalCartao / totalMetodos) * 100 : 0)} (
                  {fmtBRL(totalCartao)}) e NuPay {fmtPct(totalMetodos ? (totalNuPay / totalMetodos) * 100 : 0)}.
                </dd>
                {!soFace && (
                  <>
                    <dt>Parcelamento</dt>
                    <dd>
                      {fmtInt(pedidosParcelados)} pedidos parcelados ({fmtBRL(valorParcelado)} —{' '}
                      {fmtPct(totalMetodos ? (valorParcelado / totalMetodos) * 100 : 0)} do volume), gerando{' '}
                      {fmtBRL(t.juros)} de juros pagos pelo comprador.
                    </dd>
                    {topParcelas.length > 0 && (
                      <>
                        <dt>Faixas de parcelamento mais vendidas</dt>
                        <dd>
                          {topParcelas.map((p, i) => (
                            <span key={p.parcelas}>
                              {i > 0 ? ' · ' : ''}
                              <b>{p.parcelas}x</b>: {fmtInt(p.pedidos)} pedidos ({fmtBRL(p.total)})
                            </span>
                          ))}
                          {parcelaMedia > 0
                            ? ` · parcela média ${parcelaMedia.toLocaleString('pt-BR', {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1,
                              })}x`
                            : ''}
                        </dd>
                      </>
                    )}
                    <dt>Seguro de ingressos</dt>
                    <dd>
                      Adesão de {t.pedidosPagos > 0 ? fmtPct((t.pedidosComSeguro / t.pedidosPagos) * 100) : '—'} dos
                      pedidos ({fmtInt(t.pedidosComSeguro)} pedidos), {fmtBRL(t.seguro)} vendidos
                      {t.pedidosComSeguro > 0 ? ` · prêmio médio ${fmtBRL(t.seguro / t.pedidosComSeguro)}` : ''}.
                    </dd>
                  </>
                )}
                <dt>Meia-entrada</dt>
                <dd>
                  {fmtInt(c?.meiaEntrada.qtd ?? 0)} bilhetes (
                  {t.pagos > 0 ? fmtPct(((c?.meiaEntrada.qtd ?? 0) / t.pagos) * 100) : '—'} dos pagos),{' '}
                  {fmtBRL(c?.meiaEntrada.valor ?? 0)} de face.
                </dd>
                <dt>Gasto médio por comprador</dt>
                <dd>
                  {c?.compradoresUnicos
                    ? `${fmtBRL((soFace ? t.valorFace : t.totalCompras) / c.compradoresUnicos)} por comprador único`
                    : '—'}{' '}
                  ({fmtInt(c?.compradoresUnicos ?? 0)} compradores · {fmtBRL(t.ticketMedio)} por bilhete).
                </dd>
                <dt>Cancelamentos</dt>
                <dd>
                  {fmtInt(c?.cancelamentos.qtd ?? 0)} bilhetes cancelados
                  {t.pagos + (c?.cancelamentos.qtd ?? 0) > 0
                    ? ` (${fmtPct(((c?.cancelamentos.qtd ?? 0) / (t.pagos + (c?.cancelamentos.qtd ?? 0))) * 100)} do emitido)`
                    : ''}
                  , {fmtBRL(c?.cancelamentos.valor ?? 0)} de face.
                </dd>
              </Dl>
            </IndicatorCard>
          </Grid2>

          {/* Detalhamento por evento */}
          <SectionTitle>Detalhamento por evento</SectionTitle>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Evento</th>
                  <th>ID</th>
                  <th>Bilhetes pagos</th>
                  <th>Cortesias</th>
                  <th>Ingressos (face)</th>
                  {!soFace && (
                    <>
                      <th>Conveniência</th>
                      <th>Juros</th>
                      <th>Seguro</th>
                      <th>Taxa de transação</th>
                      <th>Total da compra</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {bordero.eventos.map((e) => (
                  <React.Fragment key={e.eventoId}>
                    <tr>
                      <td style={{ textAlign: 'left' }}>
                        {e.eventoId > 0 ? (
                          <span
                            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            onClick={() => toggleDrill(e.eventoId)}
                          >
                            <span style={{ color: '#B0020B', fontSize: 10 }}>
                              {drillEvent[e.eventoId] ? '▾' : '▸'}
                            </span>
                            {e.nome}
                          </span>
                        ) : (
                          <span style={{ fontWeight: 600, color: '#B0020B' }}>{e.nome}</span>
                        )}
                        {[e.praca, e.uf].filter(Boolean).length ? (
                          <span style={{ color: '#98a2b3' }}> · {[e.praca, e.uf].filter(Boolean).join(' · ')}</span>
                        ) : null}
                      </td>
                      <td style={{ color: '#98a2b3' }}>{e.eventoId > 0 ? e.eventoId : '—'}</td>
                      <td>{fmtInt(e.pagos)}</td>
                      <td>{fmtInt(e.cortesias)}</td>
                      <td>{fmtBRL(e.valorFace)}</td>
                      {!soFace && (
                        <>
                          <td>{fmtBRL(e.conveniencia)}</td>
                          <td>{fmtBRL(e.juros)}</td>
                          <td>{fmtBRL(e.seguro)}</td>
                          <td>{fmtBRL(e.taxaProcessamento)}</td>
                          <td>{fmtBRL(e.totalCompras)}</td>
                        </>
                      )}
                    </tr>
                    {drillEvent[e.eventoId] && (
                      <tr>
                        <td colSpan={soFace ? 5 : 10} style={{ padding: '4px 8px 12px' }}>
                          <SetorDrill
                            eventId={e.eventoId}
                            startDate={applied?.startDate}
                            endDate={applied?.endDate}
                            pdvIds={applied?.pdvIds}
                            ignorePracaTestes={applied?.ignorePracaTestes}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                <tr style={{ fontWeight: 700 }}>
                  <td style={{ textAlign: 'left' }}>
                    Total geral —{' '}
                    {isPeriodMode
                      ? `${fmtInt(bordero.meta.totalEventos ?? 0)} evento(s)`
                      : `${bordero.eventos.length} evento(s)`}
                  </td>
                  <td />
                  <td>{fmtInt(t.pagos)}</td>
                  <td>{fmtInt(t.cortesias)}</td>
                  <td>{fmtBRL(t.valorFace)}</td>
                  {!soFace && (
                    <>
                      <td>{fmtBRL(t.conveniencia)}</td>
                      <td>{fmtBRL(t.juros)}</td>
                      <td>{fmtBRL(t.seguro)}</td>
                      <td>{fmtBRL(t.taxaProcessamento)}</td>
                      <td>{fmtBRL(t.totalCompras)}</td>
                    </>
                  )}
                </tr>
              </tbody>
            </Table>
          </TableScroll>

          {/* Top N produtores (modo período) */}
          {isPeriodMode && produtores.length > 0 && (
            <>
              <SectionTitle>Top {bordero.meta.topN ?? 10} produtores</SectionTitle>
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Produtor</th>
                      <th>Eventos</th>
                      <th>Ingressos pagos</th>
                      <th>Ticket médio</th>
                      <th>Total da compra</th>
                      <th>% do total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {produtores.map((p) => (
                      <tr key={p.clienteId} style={p.clienteId === 0 ? { color: '#B0020B' } : undefined}>
                        <td style={{ textAlign: 'left' }}>{p.nome}</td>
                        <td>{fmtInt(p.eventos)}</td>
                        <td>{fmtInt(p.pagos)}</td>
                        <td>{fmtBRL(p.ticketMedio)}</td>
                        <td>{fmtBRL(p.totalCompras)}</td>
                        <td>{fmtPct(t.totalCompras > 0 ? (p.totalCompras / t.totalCompras) * 100 : 0)}</td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 700 }}>
                      <td style={{ textAlign: 'left' }}>Total geral</td>
                      <td>{fmtInt(bordero.meta.totalEventos ?? 0)}</td>
                      <td>{fmtInt(t.pagos)}</td>
                      <td>{fmtBRL(t.ticketMedio)}</td>
                      <td>{fmtBRL(t.totalCompras)}</td>
                      <td>{fmtPct(100)}</td>
                    </tr>
                  </tbody>
                </Table>
              </TableScroll>
            </>
          )}

          {/* Nota metodológica */}
          {soFace ? (
            <Note>
              <b>Nota metodológica.</b> Valor de face = soma do preço dos ingressos dos bilhetes
              ativos (situação ≠ cancelado). Não inclui taxa de conveniência, juros de parcelamento,
              seguro nem taxa de processamento.
            </Note>
          ) : (
            <Note>
              <b>Nota metodológica.</b> {bordero.meta.nota}
              {Math.abs(bordero.meta.diferencaReconciliacao) >= 0.01 && (
                <>
                  {' '}
                  Diferença de reconciliação (soma dos componentes − total pago):{' '}
                  <b>{fmtBRL(bordero.meta.diferencaReconciliacao)}</b> — resíduo de cancelamentos
                  parciais e pedidos legados, não é dinheiro perdido.
                </>
              )}
              {t.custoAdquirencia > 0 && (
                <>
                  {' '}
                  Custo de adquirência (métrica de custo, não cobrado do comprador):{' '}
                  <b>{fmtBRL(t.custoAdquirencia)}</b>.
                </>
              )}
            </Note>
          )}
          </div>
        </>
      )}
    </Page>
  )
}

export default Bordero
